import type { DefaultTheme } from 'styled-components'
import styled, { css } from 'styled-components'

import { getFontStyles } from '@helpers/styled-helpers'

import type { ButtonBaseProps } from './ButtonBase'
import { ButtonBase } from './ButtonBase'
import { BUTTON_SIZE, BUTTON_VARIANT } from './constant'

import { OutlineEffectStyles } from '@styles'

export type SizeProps = ConstObjectValues<typeof BUTTON_SIZE>
type VariantProps = ConstObjectValues<typeof BUTTON_VARIANT>

export enum StyleOnlyPropKeys {
  FULL_WIDTH = 'fullWidth',
  SIZE = 'size',
  VARIANT = 'variant',
}

export interface StyleOnlyProps {
  [StyleOnlyPropKeys.FULL_WIDTH]?: boolean
  [StyleOnlyPropKeys.SIZE]?: SizeProps
  [StyleOnlyPropKeys.VARIANT]?: VariantProps
}

export interface StyleProps extends StyleOnlyProps {
  theme: DefaultTheme
}

export const BUTTON_CSS_VARIABLES = {
  backgroundColor: '--button-background-color',
  borderColor: '--button-border-color',
  color: '--button-color',
  disabledBackgroundColor: '--button-disabled-background-color',
  disabledBorderColor: '--button-disabled-border-color',
  disabledColor: '--button-disabled-color',
  hoverBackgroundColor: '--button-hover-background-color',
  hoverBorderColor: '--button-hover-border-color',
  activeBackgroundColor: '--button-active-background-color',
  focusBorderColor: '--button-focus-border-color',
}

function getButtonSizeStyles({ theme, size }: StyleProps) {
  switch (size) {
    case BUTTON_SIZE.LARGE:
      return css`
        height: 52px;
        min-width: 100px;
        padding: 12px 20px;
        ${getFontStyles(theme, 'navigation-lg')}
      `

    case BUTTON_SIZE.SMALL:
      return css`
        height: 32px;
        min-width: 60px;
        padding: 4px 12px;
        ${getFontStyles(theme, 'navigation-md')}
      `

    case BUTTON_SIZE.NORMAL:
    default:
      return css`
        height: 40px;
        min-width: 80px;
        padding: 8px 16px;
        ${getFontStyles(theme, 'navigation-md')}
      `
  }
}

export function getButtonVariantStyles({ theme: { colors }, variant }: StyleProps) {
  let activeBackgroundColor = 'transparent'
  let backgroundColor = 'transparent'
  let borderColor = backgroundColor
  let disabledBackgroundColor = 'transparent'
  let disabledBorderColor = 'transparent'
  let focusBorderColor = 'inherit'
  let fontColor = 'inherit'
  let hoverBackgroundColor = 'transparent'
  let hoverBorderColor = hoverBackgroundColor

  switch (variant) {
    case BUTTON_VARIANT.ALERT:
      activeBackgroundColor = focusBorderColor = colors.gray[30]
      fontColor = borderColor = colors.error[50]
      hoverBackgroundColor = hoverBorderColor = colors.gray[2]
      break

    case BUTTON_VARIANT.ALERT_TEXT:
      activeBackgroundColor = focusBorderColor = colors.gray[30]
      fontColor = colors.error[50]
      hoverBackgroundColor = hoverBorderColor = colors.gray[2]
      break

    case BUTTON_VARIANT.SECONDARY_CONTAINED:
      activeBackgroundColor = borderColor = disabledBorderColor = focusBorderColor = colors.gray[30]
      backgroundColor = disabledBackgroundColor = colors.gray[0]
      fontColor = colors.gray[70]
      hoverBackgroundColor = hoverBorderColor = colors.gray[2]
      break

    case BUTTON_VARIANT.PRIMARY_TEXT:
      activeBackgroundColor = colors.mango[30]
      focusBorderColor = colors.primary[30]
      fontColor = colors.primary[50]
      hoverBackgroundColor = hoverBorderColor = colors.mango[20]
      break

    case BUTTON_VARIANT.PRIMARY_CONTAINED:
      activeBackgroundColor = colors.primary[60]
      backgroundColor = colors.primary[50]
      disabledBackgroundColor = disabledBorderColor = colors.gray[30]
      focusBorderColor = colors.primary[30]
      fontColor = colors.gray[0]
      hoverBackgroundColor = hoverBorderColor = colors.primary[40]
      break

    case BUTTON_VARIANT.PRIMARY_CONTAINED_TRANSPARENT:
      borderColor = colors.gray[40]
      disabledBackgroundColor = disabledBorderColor = colors.gray[30]
      focusBorderColor = 'transparent'
      hoverBorderColor = colors.primary[40]
      break

    case BUTTON_VARIANT.SECONDARY_TEXT:
    default:
      activeBackgroundColor = focusBorderColor = colors.gray[30]
      fontColor = colors.gray[70]
      hoverBackgroundColor = hoverBorderColor = colors.gray[2]
      break

    // TODO new variant?
    case BUTTON_VARIANT.TEXT:
      activeBackgroundColor = focusBorderColor = 'transparent'
      fontColor = 'inherit'
      hoverBackgroundColor = hoverBorderColor = 'transparent'
      break
  }

  return css`
    ${BUTTON_CSS_VARIABLES.backgroundColor}: ${backgroundColor};
    ${BUTTON_CSS_VARIABLES.borderColor}: ${borderColor};
    ${BUTTON_CSS_VARIABLES.color}: ${fontColor};
    ${BUTTON_CSS_VARIABLES.disabledBackgroundColor}: ${disabledBackgroundColor};
    ${BUTTON_CSS_VARIABLES.disabledBorderColor}: ${disabledBorderColor};
    ${BUTTON_CSS_VARIABLES.disabledColor}: ${colors.gray[40]};
    ${BUTTON_CSS_VARIABLES.hoverBackgroundColor}: ${hoverBackgroundColor};
    ${BUTTON_CSS_VARIABLES.hoverBorderColor}: ${hoverBorderColor};
    ${BUTTON_CSS_VARIABLES.activeBackgroundColor}: ${activeBackgroundColor};
    ${BUTTON_CSS_VARIABLES.focusBorderColor}: ${focusBorderColor};

    background-color: var(${BUTTON_CSS_VARIABLES.backgroundColor});
    border-color: var(${BUTTON_CSS_VARIABLES.borderColor});
    color: var(${BUTTON_CSS_VARIABLES.color});

    ${OutlineEffectStyles}

    &:not(:disabled) {
      &:hover,
      &:focus:not(:active) {
        background-color: var(${BUTTON_CSS_VARIABLES.hoverBackgroundColor});
        border-color: var(${BUTTON_CSS_VARIABLES.hoverBorderColor});
      }
      &:active,
      &:hover:active,
      &:focus:active {
        background-color: var(${BUTTON_CSS_VARIABLES.activeBackgroundColor});
        border-color: var(${BUTTON_CSS_VARIABLES.activeBackgroundColor});
      }
      &:focus:not(:active) {
        &::before {
          border-color: var(${BUTTON_CSS_VARIABLES.focusBorderColor});
        }
      }
    }
    &:disabled {
      background-color: var(${BUTTON_CSS_VARIABLES.disabledBackgroundColor});
      border-color: var(${BUTTON_CSS_VARIABLES.disabledBorderColor});
      color: var(${BUTTON_CSS_VARIABLES.disabledColor});
    }
  `
}

export interface ButtonProps extends ButtonBaseProps, StyleOnlyProps {}

/**
 * Button component
 *
 * @param {boolean} fullWidth - make block element
 * @param {string} size - normal or small
 * @param {string} variant - primaryContained, primaryText, secondaryContained, secondaryText, alert, alertText
 */
export const Button = styled(ButtonBase).withConfig<ButtonProps>({
  shouldForwardProp: (prop, defaultValidator) => {
    return !(Object.values(StyleOnlyPropKeys) as unknown[]).includes(prop) && defaultValidator(prop)
  },
})`
  justify-content: center;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'unset')};

  ${getButtonSizeStyles}
  ${getButtonVariantStyles}
`
